import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concat, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { set_company_preferences_success } from '@rpt-ngrx/actions/company.action';
import { set_date_type_to_custom } from '@rpt-ngrx/actions/filters.action';
import { clear_selected_data } from '@rpt-ngrx/actions/select.action';
import { force_disable_content_loader } from '@rpt-ngrx/actions/ui.action';
import { selectFilterState } from '@rpt-ngrx/selectors/filters.selector';
import { PostMessageService } from '@services/post-message';
import { ReportsService } from '@shared/services';

import {
    add_sites,
    add_sites_failure,
    add_sites_success,
    cleanup_filter_on_destroy,
    GET_RECENT_SITES_LIST_SUCCESS,
    get_recent_sites_success,
    remember_selected_sites,
    SHOW_SITE_SELECTOR,
    show_site_selector,
    show_site_selector_finish,
    site_selection,
    site_selection_from_portal,
    site_selection_success,
    SITE_SELECTION_SUCCESS,
    update_site_selection,
    update_site_selection_failure,
    update_site_selection_success,
} from '../actions/sites.action';

@Injectable()
export class SitesEffects {
    getRecentSitesList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GET_RECENT_SITES_LIST_SUCCESS),
            map((msg) => get_recent_sites_success(msg)),
        );
    });

    siteSelectionSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SITE_SELECTION_SUCCESS),
            map((msg) => site_selection(msg)),
        );
    });

    siteSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(site_selection),
            map((msg) => site_selection_success(msg)),
        );
    });

    addSites$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(add_sites),
            map(({ sites }) => {
                if (sites?.length) {
                    return add_sites_success({ sites });
                }
                return add_sites_failure();
            }),
        );
    });

    addSitesFailure$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(add_sites_failure),
            tap(() => {
                console.log('errpr');
                this.router.navigate(['/pages/error-page'], { queryParams: { type: 'no-sites' } });
            }),
            map(() => force_disable_content_loader()),
        );
    });

    showSitesSelector$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(show_site_selector),
            tap(({ options, msgOptions }) => {
                this.postMessageService.sendMessage(SHOW_SITE_SELECTOR, msgOptions.messageId, options);
            }),
            map(() => show_site_selector_finish()),
        );
    });

    updateSiteSelection$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(update_site_selection),
            map(({ sites, prevSelectedSites, prevSelectedSite, isMultiple }) =>
                update_site_selection_success({ sites, prevSelectedSites, prevSelectedSite, isMultiple }),
            ),
            catchError(async () => update_site_selection_failure),
        );
    });

    setDateTypeToCustom$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(update_site_selection, site_selection_from_portal),
            concatLatestFrom(() => this.store.select(selectFilterState)),
            filter(([_action, stateData]) => !!stateData?.selectedDateForMultiDate),
            map(([_action, stateData]) =>
                set_date_type_to_custom({ selectedDate: stateData?.selectedDateForMultiDate, isDrillDown: false }),
            ),
        );
    });

    onCompaniesSet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(set_company_preferences_success),
            switchMap(() => this.reportsService.fetchSites().pipe(map((sites) => add_sites({ sites })))),
        );
    });

    cleanupFilterOnDestroy$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(cleanup_filter_on_destroy),
            mergeMap(({ selectedSite, selectedSites }) =>
                concat(of(remember_selected_sites({ selectedSite, selectedSites })), of(clear_selected_data())),
            ),
        );
    });

    constructor(
        private reportsService: ReportsService,
        private postMessageService: PostMessageService,
        private actions$: Actions,
        private router: Router,
        private store: Store,
    ) {}
}
